import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, IconButton, MenuItem, Typography } from '@mui/material';
import {
  CallOutlined,
  ChevronLeft,
  Close,
  ContactSupport,
  RadioButtonChecked,
  RadioButtonUnchecked,
} from '@mui/icons-material';
import MobilePackageInfo from '@src/containers/MobilePackageInfo';
import logoAiVoice from '@src/assets/logo/logo-AiVoice-new.svg';
import i18n from '@src/languages';
import useWindowDimensions from '@src/hooks/useWindowDimensions';
import { getCookie, setCookie } from '@src/utils/cookie';
import {
  CUSTOMER_SUPPORT_PHONE_NUMBER,
  SUPPORT_CENTER_URL,
  VBEE_DOMAIN,
  VBEE_HOTLINE,
} from '@src/configs';
import { TIME_STORE_LANGUAGE } from '@src/constants/device';
import { COUNTRY, LANGUAGE } from '@src/constants';
import { COLOR } from '@src/styles/color';
import {
  StyledMobileProfileAction,
  StyledMobileProfileContent,
} from './index.style';
import UserActions from './UserActions';

const languages = [
  {
    value: LANGUAGE.VN,
    country: COUNTRY.VN,
    label: 'vietnamese',
    icon: 'https://vbee.s3.ap-southeast-1.amazonaws.com/images/nations/round/vn.png',
  },
  {
    value: LANGUAGE.EN,
    country: COUNTRY.EN,
    label: 'english',
    icon: 'https://vbee.s3.ap-southeast-1.amazonaws.com/images/nations/round/gb.png',
  },
  // {
  //   value: LANGUAGE.PH,
  //   country: COUNTRY.PH,
  //   label: 'filipino',
  //   icon: 'https://vbee.s3.ap-southeast-1.amazonaws.com/images/nations/Philippines.png',
  // },
  // {
  //   value: LANGUAGE.TH,
  //   country: COUNTRY.TH,
  //   label: 'thailand',
  //   icon: 'https://vbee.s3.ap-southeast-1.amazonaws.com/images/nations/Thailand.png',
  // },
  // {
  //   value: LANGUAGE.CN,
  //   country: COUNTRY.CN,
  //   label: 'chinese',
  //   icon: 'https://vbee.s3.ap-southeast-1.amazonaws.com/images/nations/China.png',
  // },
];

const Setting = ({ onShowProfile, location }) => (
  <Box marginTop="-8px">
    <UserActions onShowProfile={onShowProfile} location={location} />
  </Box>
);

const LanguageSetting = ({ location, handleGoBackProfile }) => {
  const [language, setLanguage] = useState({});
  const { t } = useTranslation();

  const handleChangeLanguage = (lng) => {
    i18n.changeLanguage(lng.value);
    setLanguage(lng);
  };

  const handleClickChangeLanguage = (lng) => {
    handleChangeLanguage(lng);
    setCookie({
      cname: 'lng',
      cvalue: lng.value,
      domain: VBEE_DOMAIN,
      extime: TIME_STORE_LANGUAGE,
    });
  };

  useEffect(() => {
    const defaultLanguageValue = localStorage.getItem('i18nextLng');
    const defaultLanguage = languages.find(
      (lng) => lng.value === defaultLanguageValue,
    );

    const currentLanguageValue = getCookie('lng');
    const currentLanguage = languages.find(
      (lng) => lng.value === currentLanguageValue,
    );

    if (location) {
      const { country: countryCode } = location;
      localStorage.setItem('country', COUNTRY[countryCode] || COUNTRY.EN);
    }

    if (currentLanguage) {
      handleChangeLanguage(currentLanguage);
      return;
    }

    const countryLanguageValue = localStorage.getItem('country');
    const countryLanguage = languages.find(
      (lng) => lng.country === countryLanguageValue,
    );
    if (countryLanguage) {
      handleChangeLanguage(countryLanguage);
      return;
    }

    if (defaultLanguage) {
      setLanguage(defaultLanguage);
      return;
    }
    handleChangeLanguage(languages[0]);
  }, [location]);

  return (
    <Box className="sub-setting">
      <Box className="sub-setting-back" onClick={handleGoBackProfile}>
        <ChevronLeft className="icon" />
        <Typography className="text">{t('language')}</Typography>
      </Box>
      {languages.map((lng) => (
        <MenuItem
          className={`sub-setting-item ${
            lng.value === language.value
              ? 'selected-language'
              : 'unselected-language'
          }`}
          onClick={() => handleClickChangeLanguage(lng)}
        >
          <Box display="flex" gap="12px">
            {lng.value === language.value ? (
              <RadioButtonChecked />
            ) : (
              <RadioButtonUnchecked color={COLOR.label[80]} />
            )}
            {t(lng.label)}
          </Box>
        </MenuItem>
      ))}
    </Box>
  );
};

const HelpSetting = ({ handleGoBackProfile }) => {
  const { t } = useTranslation();

  return (
    <Box className="sub-setting">
      <Box className="sub-setting-back" onClick={handleGoBackProfile}>
        <ChevronLeft className="icon" />
        <Typography className="text">{t('help')}</Typography>
      </Box>
      <a
        href={SUPPORT_CENTER_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="text-link"
      >
        <MenuItem className="item">
          <ContactSupport />
          {t('supportCenter')}
        </MenuItem>
      </a>
      <a className="text-link" href={`tel:${CUSTOMER_SUPPORT_PHONE_NUMBER}`}>
        <MenuItem className="item">
          <CallOutlined />
          <Typography>
            {t('customerSupport')}: {CUSTOMER_SUPPORT_PHONE_NUMBER}
          </Typography>
        </MenuItem>
      </a>
      <a className="text-link" href={`tel:${VBEE_HOTLINE}`}>
        <MenuItem className="item">
          <CallOutlined />
          <Typography>
            {t('hotline')}: {VBEE_HOTLINE}
          </Typography>
        </MenuItem>
      </a>
    </Box>
  );
};

const MobileProfileAction = ({ onShowProfile, location }) => {
  const [screenOpen, setScreenOpen] = useState('profile');
  const { headerVoucher } = useSelector((state) => state.voucher);
  const { height: heightScreen } = useWindowDimensions();
  const handleGoBackProfile = () => setScreenOpen('profile');

  return (
    <StyledMobileProfileAction
      heightScreen={heightScreen}
      headerVoucher={headerVoucher}
      sx={{
        display: { md: 'none', xs: 'block', sm: 'none' },
      }}
      variant="persistent"
      anchor="bottom"
      open
      ModalProps={{
        keepMounted: true,
      }}
    >
      <StyledMobileProfileContent
        heightScreen={heightScreen}
        headerVoucher={headerVoucher}
      >
        {screenOpen === 'profile' && (
          <>
            <div className="mobile package-wrapper-mobile">
              <img
                src={logoAiVoice}
                alt="vbee tts logo"
                className="logo-img"
                width={108}
              />
              <IconButton
                aria-label="close"
                onClick={onShowProfile}
                color="secondary"
                variant="outlined"
              >
                <Close className="close-button" fontSize="small" />
              </IconButton>
            </div>
            <div className="new-profile-mobile">
              <MobilePackageInfo />
              <Setting onShowProfile={onShowProfile} location={location} />
            </div>
          </>
        )}
        {screenOpen === 'language' && (
          <LanguageSetting
            location={location}
            handleGoBackProfile={handleGoBackProfile}
            setScreenOpenMobile={setScreenOpen}
          />
        )}
        {screenOpen === 'help' && (
          <HelpSetting handleGoBackProfile={handleGoBackProfile} />
        )}
      </StyledMobileProfileContent>
    </StyledMobileProfileAction>
  );
};

export default MobileProfileAction;
